import { Button, Chip, Select, Spinner } from '@sjuni/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Check, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { FormError } from '@/components/FormError';
import { ApiRequestError, apiFetch } from '@/lib/api/client';
import { adminKeys } from '@/lib/api/resource';

import type { ImportAnalysis, ImportResult } from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §5.5 — the CSV importer.
 *
 * "Bulk CSV import with a column-mapping step, a dry-run preview showing what
 * would be created vs. updated, and per-row error reporting."
 *
 * Three steps because that sentence names three, and because the order matters:
 * nothing is written until somebody has seen both what the columns were read as
 * and what the run would do. The commit button is the only one that writes, and
 * it does not appear until the preview has been produced.
 */
type Step = 'choose' | 'map' | 'preview' | 'done';

export function StaffImportWizard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('choose');
  const [file, setFile] = useState<File | null>(null);
  const [analysis, setAnalysis] = useState<ImportAnalysis | null>(null);
  /** Column index (as a string key) → field name, or `''` for "skip". */
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<ImportResult | null>(null);
  const [committed, setCommitted] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  const analyse = useMutation({
    mutationFn: (chosen: File) => {
      const body = new FormData();
      body.append('file', chosen);

      return apiFetch<ImportAnalysis>('/admin/staff/import/analyse', { method: 'POST', body });
    },
    onSuccess: (result) => {
      setAnalysis(result);
      setMapping(result.suggested_mapping);
      setStep('map');
    },
  });

  const run = useMutation({
    mutationFn: ({ chosen, dryRun }: { chosen: File; dryRun: boolean }) => {
      const body = new FormData();
      body.append('file', chosen);
      body.append('dry_run', dryRun ? '1' : '0');

      for (const [index, field] of Object.entries(mapping)) {
        if (field !== '') {
          body.append(`mapping[${index}]`, field);
        }
      }

      return apiFetch<ImportResult>('/admin/staff/import', { method: 'POST', body });
    },
  });

  function fail(cause: unknown): void {
    setError(
      cause instanceof ApiRequestError && cause.message !== ''
        ? cause.message
        : t('errors.generic'),
    );
  }

  function choose(chosen: File | undefined): void {
    if (!chosen) {
      return;
    }

    setError(null);
    setFile(chosen);
    analyse.mutate(chosen, { onError: fail });
  }

  function toPreview(): void {
    if (!file) {
      return;
    }

    setError(null);

    run.mutate(
      { chosen: file, dryRun: true },
      {
        onSuccess: (result) => {
          setPreview(result);
          setStep('preview');
        },
        onError: fail,
      },
    );
  }

  function commit(): void {
    if (!file) {
      return;
    }

    setError(null);

    run.mutate(
      { chosen: file, dryRun: false },
      {
        onSuccess: (result) => {
          setCommitted(result);
          setStep('done');
          // The list behind this screen is now wrong.
          void queryClient.invalidateQueries({ queryKey: adminKeys.resource('staff') });
        },
        onError: fail,
      },
    );
  }

  // Every mapped column, so the same field cannot be claimed twice — two
  // columns writing into `email` would silently keep whichever came last.
  const claimed = new Set(Object.values(mapping).filter((field) => field !== ''));

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-h2 font-extrabold text-primary">{t('staff.import.title')}</h1>
          <p className="mt-1 text-body-sm text-secondary">{t('staff.import.intro')}</p>
        </div>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          iconLeft={<ArrowLeft size={16} strokeWidth={1.75} aria-hidden="true" />}
          onClick={() => navigate('/staff')}
        >
          {t('common.back')}
        </Button>
      </div>

      <Steps current={step} />

      <FormError message={error} />

      {step === 'choose' ? (
        <section className="rounded-card border border-subtle p-6">
          <h2 className="text-h4 font-extrabold text-primary">{t('staff.import.choose.title')}</h2>
          <p className="mt-1 text-body-sm text-secondary">{t('staff.import.choose.body')}</p>

          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv,text/plain"
            className="sr-only"
            onChange={(event) => choose(event.target.files?.[0])}
          />

          <Button
            type="button"
            className="mt-4"
            iconLeft={<Upload size={16} strokeWidth={1.75} aria-hidden="true" />}
            disabled={analyse.isPending}
            onClick={() => inputRef.current?.click()}
          >
            {t('staff.import.choose.action')}
          </Button>

          {analyse.isPending ? (
            <span className="ms-3 inline-flex items-center gap-2 text-body-sm text-secondary">
              <Spinner size="sm" label={t('staff.import.reading')} />
              {t('staff.import.reading')}
            </span>
          ) : null}
        </section>
      ) : null}

      {step === 'map' && analysis ? (
        <section className="space-y-4">
          <div>
            <h2 className="text-h4 font-extrabold text-primary">{t('staff.import.map.title')}</h2>
            <p className="mt-1 text-body-sm text-secondary">
              {t('staff.import.map.body', { count: analysis.total, file: file?.name ?? '' })}
            </p>
          </div>

          <div className="overflow-x-auto rounded-card border border-subtle">
            <table className="w-full text-body-sm">
              <caption className="sr-only">{t('staff.import.map.title')}</caption>
              <thead className="bg-surface-subtle border-b border-subtle">
                <tr>
                  <th scope="col" className="p-3 text-start font-extrabold text-primary">
                    {t('staff.import.map.column')}
                  </th>
                  <th scope="col" className="p-3 text-start font-extrabold text-primary">
                    {t('staff.import.map.sample')}
                  </th>
                  <th scope="col" className="p-3 text-start font-extrabold text-primary">
                    {t('staff.import.map.field')}
                  </th>
                </tr>
              </thead>

              <tbody>
                {analysis.headers.map((header, index) => {
                  const key = String(index);
                  const current = mapping[key] ?? '';

                  return (
                    <tr key={key} className="border-b border-subtle last:border-0">
                      <td className="p-3 font-semibold text-primary">{header}</td>

                      <td className="p-3 text-secondary">
                        {analysis.sample
                          .map((row) => row[index])
                          .filter((cell) => cell !== undefined && cell !== '')
                          .slice(0, 2)
                          .join(' · ') || '—'}
                      </td>

                      <td className="p-3">
                        <Select
                          label={t('staff.import.map.fieldFor', { column: header })}
                          hideLabel
                          value={current}
                          onChange={(event) =>
                            setMapping({ ...mapping, [key]: event.target.value })
                          }
                          options={[
                            { value: '', label: t('staff.import.map.skip') },
                            ...analysis.fields.map((field) => ({
                              value: field,
                              label: t(`staff.import.fields.${field}`, field),
                              // Taken by another column already.
                              disabled: field !== current && claimed.has(field),
                            })),
                          ]}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" onClick={toPreview} disabled={run.isPending || !claimed.size}>
              {t('staff.import.map.action')}
            </Button>

            {run.isPending ? <Spinner size="sm" label={t('staff.import.running')} /> : null}

            {claimed.has('last_name_ka') ? null : (
              <span className="text-caption text-muted">{t('staff.import.map.needSurname')}</span>
            )}
          </div>
        </section>
      ) : null}

      {step === 'preview' && preview ? (
        <section className="space-y-4">
          <div>
            <h2 className="text-h4 font-extrabold text-primary">
              {t('staff.import.preview.title')}
            </h2>
            {/*
              Said plainly, because the difference between this screen and the
              next one is the whole point of the dry run.
            */}
            <p className="mt-1 text-body-sm text-secondary">{t('staff.import.preview.body')}</p>
          </div>

          <Summary result={preview} />
          <Rows result={preview} />

          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" variant="secondary" onClick={() => setStep('map')}>
              {t('staff.import.preview.back')}
            </Button>

            <Button
              type="button"
              onClick={commit}
              disabled={run.isPending || preview.summary.created + preview.summary.updated === 0}
            >
              {t('staff.import.preview.action', {
                count: preview.summary.created + preview.summary.updated,
              })}
            </Button>

            {run.isPending ? <Spinner size="sm" label={t('staff.import.running')} /> : null}
          </div>

          {preview.summary.failed > 0 ? (
            <p className="text-body-sm text-secondary">
              {/*
                The failed rows are skipped, not fatal — §5.5 asks for per-row
                reporting, which means the good rows still go in. Unless there
                are none, in which case saying "the rest will be saved" beside a
                disabled button that would save nothing is just confusing.
              */}
              {preview.summary.created + preview.summary.updated > 0
                ? t('staff.import.preview.skipped', { count: preview.summary.failed })
                : t('staff.import.preview.allFailed')}
            </p>
          ) : null}
        </section>
      ) : null}

      {step === 'done' && committed ? (
        <section className="space-y-4">
          <h2 className="flex items-center gap-2 text-h4 font-extrabold text-primary">
            <Check size={20} strokeWidth={2} aria-hidden="true" />
            {t('staff.import.done.title')}
          </h2>

          {/* Drafts, deliberately — see `StaffImporter`. */}
          <p className="text-body-sm text-secondary">{t('staff.import.done.body')}</p>

          <Summary result={committed} />
          <Rows result={committed} />

          <Button type="button" onClick={() => navigate('/staff')}>
            {t('staff.import.done.action')}
          </Button>
        </section>
      ) : null}
    </div>
  );
}

function Steps({ current }: { current: Step }) {
  const { t } = useTranslation();

  const order: Step[] = ['choose', 'map', 'preview', 'done'];
  const index = order.indexOf(current);

  return (
    <ol className="flex flex-wrap gap-2" aria-label={t('staff.import.steps')}>
      {order.map((step, position) => (
        <li key={step}>
          <Chip tone={position === index ? 'navy' : 'neutral'}>
            <span aria-current={position === index ? 'step' : undefined}>
              {`${position + 1}. ${t(`staff.import.step.${step}`)}`}
            </span>
          </Chip>
        </li>
      ))}
    </ol>
  );
}

function Summary({ result }: { result: ImportResult }) {
  const { t } = useTranslation();

  const cells = [
    { key: 'created', value: result.summary.created },
    { key: 'updated', value: result.summary.updated },
    { key: 'failed', value: result.summary.failed },
  ];

  return (
    <dl className="grid gap-3 sm:grid-cols-3">
      {cells.map((cell) => (
        <div key={cell.key} className="rounded-card border border-subtle p-4">
          <dt className="text-caption text-muted">{t(`staff.import.summary.${cell.key}`)}</dt>
          <dd className="mt-1 text-h3 font-extrabold text-primary">{cell.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** §5.5's "per-row error reporting", with the failures first. */
function Rows({ result }: { result: ImportResult }) {
  const { t } = useTranslation();

  const rows = [...result.rows].sort((a, b) =>
    a.action === b.action ? a.line - b.line : a.action === 'error' ? -1 : 1,
  );

  if (rows.length === 0) {
    return null;
  }

  return (
    <div className="max-h-96 overflow-y-auto rounded-card border border-subtle">
      <table className="w-full text-body-sm">
        <caption className="sr-only">{t('staff.import.rows.caption')}</caption>
        <thead className="bg-surface-subtle sticky top-0 border-b border-subtle">
          <tr>
            <th scope="col" className="p-3 text-start font-extrabold text-primary">
              {t('staff.import.rows.line')}
            </th>
            <th scope="col" className="p-3 text-start font-extrabold text-primary">
              {t('staff.import.rows.name')}
            </th>
            <th scope="col" className="p-3 text-start font-extrabold text-primary">
              {t('staff.import.rows.outcome')}
            </th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => (
            <tr key={row.line} className="border-b border-subtle last:border-0">
              <td className="p-3 tabular-nums text-secondary">{row.line}</td>
              <td className="p-3 text-primary">{row.name || '—'}</td>

              <td className="p-3">
                {row.action === 'error' ? (
                  <span className="flex items-start gap-2 text-primary">
                    <AlertTriangle
                      size={16}
                      strokeWidth={1.75}
                      aria-hidden="true"
                      className="mt-0.5 shrink-0"
                    />
                    <span>
                      {/* Every reason, not the first — a row usually has more
                          than one thing wrong with it. */}
                      {row.errors.join(' ')}
                    </span>
                  </span>
                ) : (
                  <Chip tone={row.action === 'create' ? 'navy' : 'gold'}>
                    {t(`staff.import.actions.${row.action}`)}
                  </Chip>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
